import { useState, useEffect, useRef } from 'react';
import { sosGroups, type SOSOption, type SOSFieldData } from '../../utils/sosData';
import { sosService, type SOSDisease } from '../../services/sosService';
import { sosConsultationService } from '../../services/sosConsultationService';
import SOSResultModal from './SOSResultModal';
import { featureGateService, type FeatureAccessResult } from '../../services/featureGateService';
import { useAuth } from '../../contexts/AuthContext';
import { UpgradeModal } from '../payment/UpgradeModal';
import { useSubscription } from '../../hooks/useSubscription';

interface SOSModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SOSFormData {
  [key: string]: string | string[];
}

const buildFields = (diseases: SOSDisease[], selectedDisease?: SOSDisease | null): SOSFieldData[] => {
  const diseaseOptions: SOSOption[] = diseases
    .filter((disease) => disease.active)
    .map((disease) => ({
      id: disease.id,
      label: disease.name,
      description: disease.description,
    }));

  return [
    {
      id: 'enfermidade_doenca',
      title: 'Enfermidade/Doença',
      placeholder: 'Selecione a enfermidade principal',
      required: true,
      options: diseaseOptions,
    },
    ...sosGroups.map((group) => ({
      id: group.id,
      title: group.title,
      placeholder: group.placeholder,
      required: group.required,
      options: selectedDisease ? [...(selectedDisease.groups[group.id] || [])] : [],
    })),
  ];
};

export default function SOSModal({ isOpen, onClose }: SOSModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<SOSFormData>({});
  const [observations, setObservations] = useState('');
  const [dynamicFieldsData, setDynamicFieldsData] = useState<SOSFieldData[]>(buildFields([]));
  const [diseases, setDiseases] = useState<SOSDisease[]>([]);
  const [loadingDiseases, setLoadingDiseases] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [recommendedRemedy, setRecommendedRemedy] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [consultationResult, setConsultationResult] = useState<{
    remedy_name: string;
    disease_name: string;
    consultation_date: string;
  } | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [featureUsage, setFeatureUsage] = useState({ used: 0, limit: 3 });
  const { subscription } = useSubscription();
  const [quotaInfo, setQuotaInfo] = useState<FeatureAccessResult | null>(null);

  // Reset form and fetch diseases when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setFormData({});
    setObservations('');
    setSuccessMessage(null);
    setErrorMessage(null);
    setRecommendedRemedy(null);
    setSearchTerms({});
    setShowResultModal(false);
    setConsultationResult(null);
    setShowTooltip(false);

    const fetchDiseases = async () => {
      setLoadingDiseases(true);
      setErrorMessage(null);
      try {
        const data = await sosService.getDiseases();
        setDiseases(data);
        setDynamicFieldsData(buildFields(data));
        setOpenDropdownId(null);
      } catch (error) {
        console.error('Erro ao carregar doenças SOS:', error);
        setErrorMessage('Não foi possível carregar as doenças. Tente novamente em instantes.');
        setDiseases([]);
        setDynamicFieldsData(buildFields([]));
      } finally {
        setLoadingDiseases(false);
      }
    };

    fetchDiseases();
  }, [isOpen]);

  // Buscar cota atual ao abrir modal (evita refetch contínuo)
  const lastQuotaUserIdRef = useRef<string | null>(null);
  const quotaFetchInFlightRef = useRef<boolean>(false);
  useEffect(() => {
    if (!isOpen) return;
    const uid = user?.id || null;
    if (!uid) { setQuotaInfo(null); return; }
    // Evitar apenas concorrência; sempre refazer a leitura ao abrir
    if (quotaFetchInFlightRef.current) return;

    lastQuotaUserIdRef.current = uid;
    quotaFetchInFlightRef.current = true;
    featureGateService
      .canUseFeature(uid, 'sos_consultation')
      .then((res) => {
        setQuotaInfo((prev) => {
          if (!prev) return res;
          const same = prev.allowed === res.allowed &&
                      prev.limit === res.limit &&
                      prev.used === res.used &&
                      prev.quota_type === res.quota_type;
          return same ? prev : res;
        });
      })
      .catch((err) => console.warn('[SOS] Falha ao obter quota:', err))
      .finally(() => { quotaFetchInFlightRef.current = false; });
  }, [isOpen, user?.id]);

  // UI helper: badge de quota na header
  const renderQuotaBadge = () => {
    const q = quotaInfo;
    if (!q) return null;
    
    // Premium tem limit 999999 = ilimitado
    const limit = q.limit ?? 0;
    const isUnlimited = limit >= 999999 || !Number.isFinite(limit);
    
    if (isUnlimited) {
      return (
        <div className="inline-flex items-center gap-2 bg-white/20 text-white px-3 py-1.5 rounded-full text-xs">
          <i className="ri-infinity-line"></i>
          <span>Ilimitado</span>
        </div>
      );
    }

    const _used = q.used ?? 0;
    const remaining = q.remaining ?? 0;
    const isMonthly = q.quota_type === 'monthly';
    let renewText: string | null = null;
    if (isMonthly) {
      const now = new Date();
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      renewText = `Renova em ${nextMonthStart.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
    }

    // Parse breakdown do backend se disponível (ex: "2 crédito(s) — plano: 0, avulso: 2")
    const breakdown = q.message;
    const hasBreakdown = breakdown && breakdown.includes('plano:') && breakdown.includes('avulso:');

    return (
      <div className="inline-flex flex-col gap-1 bg-white/20 text-white px-3 py-1.5 rounded-full text-xs">
        <div className="flex items-center gap-2">
          <i className="ri-stethoscope-line"></i>
          <span className="font-semibold">{remaining} disponível</span>
        </div>
        {hasBreakdown && (
          <div className="text-[10px] opacity-90 whitespace-nowrap">
            {breakdown.split('—')[1]?.trim() || ''}
          </div>
        )}
        {isMonthly && renewText && !hasBreakdown && <div className="opacity-90 text-[10px]">{renewText}</div>}
      </div>
    );
  };

  const selectedDiseaseId = formData['enfermidade_doenca'];

  useEffect(() => {
    if (!openDropdownId) return;

    const handleClickOutside = (event: MouseEvent) => {
      const currentDropdown = dropdownRefs.current[openDropdownId];
      if (currentDropdown && !currentDropdown.contains(event.target as Node)) {
        setOpenDropdownId(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openDropdownId]);

  // Atualiza campos dinamicamente com base na doença selecionada e mantém dados consistentes
  useEffect(() => {
    const selectedDisease = diseases.find((disease) => disease.id === selectedDiseaseId) || null;
    setDynamicFieldsData(buildFields(diseases, selectedDisease));
    setOpenDropdownId(null);

    if (!selectedDisease) {
      return;
    }

    setFormData((prev) => {
      let changed = false;
      const updated: SOSFormData = { ...prev };

      for (const group of sosGroups) {
        const currentValue = updated[group.id];
        const validOptions = selectedDisease.groups[group.id] || [];
        const stillValid = validOptions.some((option) => option.id === currentValue);
        if (!stillValid && currentValue) {
          delete updated[group.id];
          changed = true;
        }
      }

      return changed ? updated : prev;
    });
  }, [diseases, selectedDiseaseId]);

  // Check if current field is filled to enable next field
  const isFieldFilled = (fieldIndex: number): boolean => {
    const field = dynamicFieldsData[fieldIndex];
    return !!formData[field.id];
  };

  // Check if field should be enabled
  const isFieldEnabled = (fieldIndex: number): boolean => {
    if (fieldIndex === 0) return true; // First field is always enabled
    
    // Check if all previous required fields are filled
    for (let i = 0; i < fieldIndex; i++) {
      const prevField = dynamicFieldsData[i];
      if (prevField.required && !formData[prevField.id]) {
        return false;
      }
    }
    return true;
  };

  // Check if form can be submitted
  const canSubmit = (): boolean => {
    // Check all required fields
    for (const field of dynamicFieldsData) {
      if (field.required && !formData[field.id]) {
        return false;
      }
    }
    return true;
  };

  const handleFieldChange = (fieldId: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    setOpenDropdownId(null);
    // Limpar busca ao selecionar
    setSearchTerms(prev => ({
      ...prev,
      [fieldId]: ''
    }));
  };

  const handleMultipleFieldChange = (fieldId: string, optionId: string, checked: boolean) => {
    setFormData(prev => {
      const currentValue = prev[fieldId];
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      
      if (checked) {
        return {
          ...prev,
          [fieldId]: [...currentArray, optionId]
        };
      } else {
        return {
          ...prev,
          [fieldId]: currentArray.filter(id => id !== optionId)
        };
      }
    });
    // Limpar busca ao selecionar
    if (checked) {
      setSearchTerms(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  // Filtrar opções baseado no termo de busca
  const getFilteredOptions = (field: SOSFieldData): SOSOption[] => {
    const searchTerm = searchTerms[field.id] || '';
    if (!searchTerm.trim()) {
      return field.options;
    }
    
    const lowerSearch = searchTerm.toLowerCase();
    return field.options.filter(option => 
      option.label.toLowerCase().includes(lowerSearch) ||
      (option.description?.toLowerCase() || '').includes(lowerSearch)
    );
  };

  const getSelectionLimit = (fieldId: string): number => {
    const selectedDisease = diseases.find((disease) => disease.id === selectedDiseaseId);
    return selectedDisease?.selectionLimits?.[fieldId] ?? 1;
  };

  const isMultipleSelection = (fieldId: string): boolean => {
    return getSelectionLimit(fieldId) > 1;
  };

  const handleSubmit = async () => {
    if (!canSubmit() || submitting) return;

    const selectedDisease = diseases.find((disease) => disease.id === selectedDiseaseId);
    if (!selectedDisease) {
      setErrorMessage('Selecione uma enfermidade válida antes de enviar.');
      return;
    }

    // ✅ VALIDAR LIMITE ANTES de criar a consulta
    if (user) {
      const access = await featureGateService.canUseFeature(user.id, 'sos_consultation');
      
      if (!access.allowed) {
        // Limite atingido - exibir modal de upgrade
        setFeatureUsage({ 
          used: access.used || 0, 
          limit: access.limit || 3 
        });
        setShowUpgradeModal(true);
        return;
      }
    }

    const { enfermidade_doenca: _enfermidade_doenca, ...symptomsSelected } = formData;

    setSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    setRecommendedRemedy(null);

    try {
      const result = await sosConsultationService.createConsultation({
        disease: selectedDisease,
        symptomsSelected,
        observations,
      });

      // ✅ Registrar uso da feature (incrementar contador)
      if (user) {
        const consume = await featureGateService.consumeFeature(user.id, 'sos_consultation');
        const success = typeof consume === 'boolean' ? consume : Boolean((consume as any)?.success);
        if (success) {
          console.log('[SOS] Feature consumida com sucesso');
          // Atualizar quota após consumo
          try {
            const refreshed = await featureGateService.canUseFeature(user.id, 'sos_consultation');
            setQuotaInfo(refreshed);
          } catch (e) {
            console.warn('[SOS] Falha ao atualizar quota após consumo:', e);
          }
        } else {
          console.warn('[SOS] Falha ao registrar uso da feature', consume);
        }
      }

      if (result.recommended_remedy_name) {
        // Preparar dados para o modal de resultado
        setConsultationResult({
          remedy_name: result.recommended_remedy_name,
          disease_name: selectedDisease.name,
          consultation_date: new Date().toISOString()
        });
        // Resetar formulário antes de abrir o modal de resultado
        setFormData({});
        setObservations('');
        setSuccessMessage(null);
        setErrorMessage(null);
        setRecommendedRemedy(null);
        setSearchTerms({});
        setOpenDropdownId(null);
        setShowTooltip(false);
        // Abrir modal de resultado
        setShowResultModal(true);
      } else {
        setSuccessMessage('Consulta registrada com sucesso!');
      }
    } catch (error) {
      console.error('Erro ao enviar consulta SOS:', error);
      const message = error instanceof Error ? error.message : 'Erro ao enviar a consulta. Tente novamente.';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleNewConsultation = () => {
    // Apenas fechar o modal de resultado
    // O formulário já está resetado e pronto para nova consulta
    setShowResultModal(false);
    setConsultationResult(null);
  };

  if (!isOpen && !showResultModal) return null;

  const getSelectedLabel = (field: SOSFieldData) => {
    const value = formData[field.id];
    if (!value || Array.isArray(value)) return null;
    return field.options.find(option => option.id === value) || null;
  };

  return (
    <>
    {isOpen && !showResultModal && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-blue-50 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-t-3xl relative z-[60]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowTooltip(!showTooltip)}
                className="relative w-10 h-10 bg-white/20 rounded-full flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors group"
                aria-label="Informações sobre o SOS"
              >
                {/* Pulso verde animado */}
                <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></div>
                <div className="absolute inset-0 rounded-full bg-green-500 opacity-50"></div>
                <i className="ri-heart-pulse-line text-2xl relative z-10"></i>
              </button>
              <div>
                <h3 className="text-xl font-bold">SOS - Médico Homeopata Virtual</h3>
                <p className="text-red-100 text-sm">Responda as perguntas para receber a indicação adequada</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          {/* Quota badge (direita, abaixo do X) */}
          <div className="mt-3 flex justify-end">
            {renderQuotaBadge()}
          </div>
          
          {/* Tooltip */}
          {showTooltip && (
            <div 
              className="absolute top-20 left-6 right-6 z-[100]"
              style={{
                animation: 'fadeInDown 200ms ease-out forwards'
              }}
            >
              <style>{`
                @keyframes fadeInDown {
                  from {
                    opacity: 0;
                    transform: translateY(-10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>
              <div className="bg-white rounded-xl shadow-2xl p-5 border-2 border-green-400 relative">
                <button
                  onClick={() => setShowTooltip(false)}
                  className="absolute top-3 right-3 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center text-gray-600 transition-colors"
                  aria-label="Fechar"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
                <div className="flex items-start gap-3 pr-8">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-information-line text-xl text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-3">Como usar o SOS</h4>
                    <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                      <p>
                        O sistema SOS foi pensado para responder com exatidão a indicação de cada doença.
                      </p>
                      <p>
                        Quanto mais preciso for a seleção dos sintomas, melhor será a avaliação do Homeopata Virtual.
                      </p>
                      <p>
                        Tome um tempo para escolher os sintomas que mais se encaixam com os que estão acontecendo e se preciso for, escreva no campo de observações mais informações que considere importante.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Content */}
        <div className="p-6 pb-32 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-4">
            {loadingDiseases ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-500 gap-3">
                <i className="ri-loader-4-line animate-spin text-2xl"></i>
                <span>Carregando doenças...</span>
              </div>
            ) : (
              dynamicFieldsData.map((field, index) => (
                <div key={field.id} className={`space-y-2 ${
                field.id === 'enfermidade_doenca' 
                  ? 'bg-red-50 border-2 border-red-200 rounded-xl p-4 shadow-md' 
                  : ''
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                    isFieldEnabled(index) 
                      ? field.id === 'enfermidade_doenca'
                        ? 'bg-red-500 text-white'
                        : 'bg-blue-500 text-white'
                      : 'bg-gray-300 text-gray-500'
                  }`}>
                    {field.id === 'enfermidade_doenca' ? '🏥' : index}
                  </div>
                  <h4 className={`font-semibold ${
                    field.id === 'enfermidade_doenca' 
                      ? 'text-xl text-red-700 font-bold' 
                      : isFieldEnabled(index) ? 'text-base text-gray-800' : 'text-base text-gray-400'
                  }`}>
                    {field.title}
                  </h4>
                  {field.required && (
                    <span className="text-red-500 text-sm">*</span>
                  )}
                  {isFieldFilled(index) && (
                    <i className="ri-check-circle-fill text-green-500 text-lg"></i>
                  )}
                </div>

                <div className={`${
                  !isFieldEnabled(index) || loadingDiseases ? 'opacity-50 pointer-events-none' : ''
                }`}>
                  {!isMultipleSelection(field.id) ? (
                    <div
                      className={`${!isFieldEnabled(index) || loadingDiseases ? 'opacity-70' : ''}`}
                      ref={(el) => {
                        dropdownRefs.current[field.id] = el;
                      }}
                    >
                      <button
                        type="button"
                        disabled={!isFieldEnabled(index) || loadingDiseases}
                        onClick={() =>
                          setOpenDropdownId((prev) =>
                            prev === field.id || !isFieldEnabled(index) ? null : field.id
                          )
                        }
                        className={`w-full p-4 rounded-xl border-2 bg-white text-left transition-all flex items-center justify-between gap-3 ${
                          formData[field.id]
                            ? field.id === 'enfermidade_doenca'
                              ? 'border-red-500 bg-red-100 text-red-800 font-semibold'
                              : 'border-blue-500 bg-blue-100 text-blue-800 font-semibold'
                            : field.id === 'enfermidade_doenca'
                              ? 'border-red-400 hover:border-red-500 focus:border-red-600'
                              : 'border-gray-300 hover:border-blue-400 focus:border-blue-500'
                        } ${
                          !isFieldEnabled(index) || loadingDiseases ? 'cursor-not-allowed opacity-60' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className={`text-sm uppercase tracking-wide ${
                            field.id === 'enfermidade_doenca' ? 'text-red-600' : 'text-blue-600'
                          }`}>
                            {field.title}
                          </p>
                          <p
                            className={`text-base ${
                              formData[field.id]
                                ? 'font-semibold'
                                : field.id === 'enfermidade_doenca'
                                  ? 'text-red-600/80'
                                  : 'text-blue-600/80'
                            }`}
                          >
                            {(() => {
                              const selectedOption = getSelectedLabel(field);
                              if (selectedOption) {
                                return selectedOption.description
                                  ? `${selectedOption.label} — ${selectedOption.description}`
                                  : selectedOption.label;
                              }
                              return field.placeholder;
                            })()}
                          </p>
                        </div>
                        <i
                          className={`ri-arrow-down-s-line text-2xl transition-transform ${
                            openDropdownId === field.id ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>

                      {openDropdownId === field.id && field.options.length > 0 && (
                        <div className="relative">
                          <div
                            className={`absolute left-0 right-0 mt-2 bg-white border-2 rounded-2xl shadow-2xl z-20 ${
                              field.id === 'enfermidade_doenca'
                                ? 'border-red-200'
                                : 'border-blue-200'
                            }`}
                          >
                            {/* Barra de busca - apenas se tiver 2 ou mais opções */}
                            {field.options.length >= 2 && (
                              <div className="sticky top-0 bg-white p-3 border-b-2 border-gray-100 rounded-t-2xl">
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={searchTerms[field.id] || ''}
                                    onChange={(e) => setSearchTerms(prev => ({
                                      ...prev,
                                      [field.id]: e.target.value
                                    }))}
                                    placeholder="Buscar..."
                                    className="w-full py-2 pl-9 pr-3 bg-gray-50 rounded-lg border border-gray-200 text-sm focus:border-blue-400 focus:outline-none focus:bg-white"
                                  />
                                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                    <i className="ri-search-line text-gray-400 text-base"></i>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            <div className="max-h-60 overflow-y-auto">
                            {getFilteredOptions(field).map((option) => {
                              const isSelected = formData[field.id] === option.id;
                              return (
                                <button
                                  key={option.id}
                                  type="button"
                                  onClick={() => handleFieldChange(field.id, option.id)}
                                  className={`w-full text-left px-4 py-3 transition-all flex flex-col gap-1 ${
                                    isSelected
                                      ? field.id === 'enfermidade_doenca'
                                        ? 'bg-red-100 text-red-700 font-semibold'
                                        : 'bg-blue-100 text-blue-700 font-semibold'
                                      : field.id === 'enfermidade_doenca'
                                        ? 'hover:bg-red-50'
                                        : 'hover:bg-blue-50'
                                  }`}
                                >
                                  <span className="text-base">{option.label}</span>
                                  {option.description && (
                                    <span className="text-sm text-gray-600 leading-snug">
                                      {option.description}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : isMultipleSelection(field.id) ? (
                    // Checkboxes para múltiplas seleções
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-2">
                        Escolha até {getSelectionLimit(field.id)} opções:
                      </p>
                      
                      {/* Barra de busca - apenas se tiver 2 ou mais opções */}
                      {field.options.length >= 2 && (
                        <div className="relative mb-3">
                          <input
                            type="text"
                            value={searchTerms[field.id] || ''}
                            onChange={(e) => setSearchTerms(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                            placeholder="Buscar sintoma..."
                            className="w-full py-3 pl-10 pr-4 bg-white rounded-xl border-2 border-gray-200 text-sm focus:border-blue-400 focus:outline-none"
                          />
                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                            <i className="ri-search-line text-gray-400 text-lg"></i>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {getFilteredOptions(field).map((option) => {
                          const currentValue = formData[field.id];
                          const selectedArray = Array.isArray(currentValue) ? currentValue : [];
                          const isChecked = selectedArray.includes(option.id);
                          const isDisabled = !isChecked && selectedArray.length >= getSelectionLimit(field.id);
                          
                          return (
                            <label
                              key={option.id}
                              className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all cursor-pointer ${
                                isChecked
                                  ? 'border-blue-500 bg-blue-50'
                                  : isDisabled
                                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={(e) => handleMultipleFieldChange(field.id, option.id, e.target.checked)}
                                className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{option.label}</div>
                                {option.description && (
                                  <div className="text-sm text-gray-600">{option.description}</div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    // Select para seleção única
                    <select
                      value={Array.isArray(formData[field.id]) ? '' : (formData[field.id] || '')}
                      onChange={(e) => handleFieldChange(field.id, e.target.value)}
                      disabled={!isFieldEnabled(index) || loadingDiseases}
                      className={`w-full p-4 rounded-xl border-2 bg-white text-gray-700 transition-all focus:outline-none ${
                        field.id === 'enfermidade_doenca'
                          ? formData[field.id]
                            ? 'border-red-500 bg-red-100 text-red-800 font-semibold'
                            : 'border-red-400 hover:border-red-500 focus:border-red-600 focus:ring-2 focus:ring-red-200'
                          : formData[field.id]
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 focus:border-blue-500'
                      }`}
                    >
                      <option value="" disabled>
                        {field.placeholder}
                      </option>
                      {field.options.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label} {option.description ? `- ${option.description}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              ))
            )}

            {/* Observations Section */}
            <div className="space-y-2 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-gray-900">
                  Observações Adicionais
                </h3>
                <span className={`text-xs font-medium ${
                  observations.length >= 200 ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {observations.length}/200
                </span>
              </div>
              <textarea
                value={observations}
                onChange={(e) => setObservations(e.target.value)}
                maxLength={200}
                placeholder="Descreva outros sintomas ou informações relevantes..."
                className="w-full min-h-[120px] p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                rows={3}
              />
              <p className="text-xs text-gray-500">
                Limite de 200 caracteres
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Submit Button */}
        <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 rounded-b-3xl">
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit() || submitting || loadingDiseases}
              className={`flex-2 py-3 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                canSubmit() && !submitting && !loadingDiseases
                  ? 'bg-red-500 text-white hover:bg-red-600 shadow-lg'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  Enviar Consulta
                </>
              )}
            </button>
          </div>

          {errorMessage && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 text-red-600 text-sm flex items-start gap-2">
              <i className="ri-error-warning-line text-lg"></i>
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="mt-4 p-4 rounded-xl bg-green-50 border-2 border-green-200">
              <div className="flex items-start gap-3">
                <i className="ri-check-circle-fill text-green-600 text-2xl"></i>
                <div className="flex-1">
                  <p className="text-green-800 font-semibold mb-1">Consulta processada com sucesso!</p>
                  {recommendedRemedy && (
                    <div className="mt-2 p-3 bg-white rounded-lg border border-green-300">
                      <p className="text-sm text-gray-600 mb-1">Remédio recomendado:</p>
                      <p className="text-xl font-bold text-green-700">{recommendedRemedy}</p>
                    </div>
                  )}
                  <button
                    onClick={handleClose}
                    className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Progress indicator */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Progresso</span>
              <span>
                {dynamicFieldsData.filter(field => field.required && formData[field.id]).length} / {dynamicFieldsData.filter(field => field.required).length} campos obrigatórios
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: `${(dynamicFieldsData.filter(field => field.required && formData[field.id]).length / dynamicFieldsData.filter(field => field.required).length) * 100}%` 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    )}
    
    {/* Modal de Resultado */}
    {consultationResult && (
      <SOSResultModal
        isOpen={showResultModal}
        onClose={() => {
          setShowResultModal(false);
          setConsultationResult(null);
          onClose(); // Fecha também o modal de formulário
        }}
        result={consultationResult}
        onNewConsultation={handleNewConsultation}
      />
    )}

    {/* Modal de Upgrade (Limite Atingido) */}
    {showUpgradeModal && (
      <UpgradeModal
        featureKey="sos_consultation"
        featureName="Consultas SOS"
        currentPlan={subscription?.plan_tier || 'free'}
        usage={featureUsage}
        onClose={() => setShowUpgradeModal(false)}
      />
    )}
    </>
  );
}
